import React, { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, X, Save, Package as PackageIcon } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const EMPTY = { name: "", description: "", price: 0, order: 0, active: true };

export default function CRMPackages() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => api.get("/crm/packages").then((r) => { setItems(r.data); setLoading(false); });
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!editing.name) { toast.error("Nome obbligatorio"); return; }
    try {
      const payload = { ...editing, price: parseFloat(editing.price) || 0, order: parseInt(editing.order) || 0 };
      if (editing.id) await api.put(`/crm/packages/${editing.id}`, payload);
      else await api.post("/crm/packages", payload);
      toast.success("Salvato");
      setEditing(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };
  const remove = async (id) => {
    if (!window.confirm("Confermi l'eliminazione?")) return;
    await api.delete(`/crm/packages/${id}`);
    toast.success("Eliminato"); load();
  };

  return (
    <div className="p-6 lg:p-10" data-testid="crm-packages-page">
      <div className="flex items-start justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="text-xs font-bold uppercase tracking-widest text-[#FF5A1F]">— Offerta</div>
          <h1 className="font-display text-5xl uppercase text-[#0A0A0A] mt-2">Pacchetti</h1>
          <p className="text-[#0A0A0A]/60 mt-2 max-w-xl">I pacchetti sono <strong>componibili</strong>: sul singolo lead in trattativa si selezionano N pacchetti e il totale si calcola automaticamente.</p>
        </div>
        <button onClick={() => setEditing({ ...EMPTY })} className="btn-cta" data-testid="pkg-add-btn">
          <Plus size={16} /> Nuovo pacchetto
        </button>
      </div>

      {loading ? <div className="text-[#0A0A0A]/50">Caricamento…</div> : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((p) => (
            <div key={p.id} className="border-2 border-[#0A0A0A] p-5 bg-[#FAFAF7]" data-testid={`pkg-card-${p.id}`}>
              <div className="flex items-start justify-between">
                <PackageIcon size={22} className="text-[#FF5A1F]" strokeWidth={1.5} />
                {!p.active && <span className="text-[10px] uppercase text-[#0A0A0A]/40">Nascosto</span>}
              </div>
              <h3 className="mt-3 font-display text-2xl uppercase text-[#0A0A0A]">{p.name}</h3>
              <div className="text-3xl font-display text-[#FF5A1F] mt-2">€{p.price?.toLocaleString('it-IT')}</div>
              {p.description && <p className="mt-3 text-sm text-[#0A0A0A]/70 leading-relaxed">{p.description}</p>}
              <div className="mt-4 flex gap-2">
                <button onClick={() => setEditing(p)} className="text-xs px-3 py-2 border-2 border-[#0A0A0A] font-bold uppercase tracking-widest flex items-center gap-1 hover:bg-[#0A0A0A] hover:text-[#FAFAF7]" data-testid={`pkg-edit-${p.id}`}>
                  <Pencil size={12} /> Modifica
                </button>
                <button onClick={() => remove(p.id)} className="text-xs px-3 py-2 border-2 border-[#0A0A0A] font-bold uppercase tracking-widest hover:bg-red-600 hover:text-white hover:border-red-600 flex items-center gap-1" data-testid={`pkg-delete-${p.id}`}>
                  <Trash2 size={12} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => setEditing(null)}>
          <div className="bg-[#FAFAF7] max-w-lg w-full max-h-[92vh] overflow-y-auto border-2 border-[#0A0A0A]" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 bg-[#0A0A0A] text-[#FAFAF7] p-4 flex justify-between">
              <h3 className="font-display text-2xl uppercase">{editing.id ? "Modifica" : "Nuovo"} pacchetto</h3>
              <button onClick={() => setEditing(null)}><X size={20} /></button>
            </div>
            <div className="p-6 space-y-3">
              <Field label="Nome" value={editing.name} onChange={(v) => setEditing({ ...editing, name: v })} testId="pkg-name" />
              <label className="block">
                <span className="text-[10px] uppercase tracking-widest font-bold text-[#0A0A0A]/60">Descrizione</span>
                <textarea value={editing.description ?? ""} onChange={(e) => setEditing({ ...editing, description: e.target.value })} rows={3} data-testid="pkg-desc" className="mt-1 block w-full border-2 border-[#0A0A0A] py-2 px-3 bg-white text-sm resize-none" />
              </label>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Prezzo (€)" type="number" value={editing.price} onChange={(v) => setEditing({ ...editing, price: v })} testId="pkg-price" />
                <Field label="Ordine" type="number" value={editing.order} onChange={(v) => setEditing({ ...editing, order: v })} testId="pkg-order" />
              </div>
              <label className="flex items-center gap-2 pt-2">
                <input type="checkbox" checked={editing.active} onChange={(e) => setEditing({ ...editing, active: e.target.checked })} data-testid="pkg-active" />
                <span className="text-sm">Attivo (disponibile per la vendita)</span>
              </label>
            </div>
            <div className="sticky bottom-0 bg-[#FAFAF7] border-t-2 border-[#0A0A0A] p-4 flex justify-end gap-3">
              <button onClick={() => setEditing(null)} className="btn-outline-dark" data-testid="pkg-cancel">Annulla</button>
              <button onClick={save} className="btn-cta" data-testid="pkg-save"><Save size={14} /> Salva</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const Field = ({ label, value, onChange, type = "text", testId }) => (
  <label className="block">
    <span className="text-[10px] uppercase tracking-widest font-bold text-[#0A0A0A]/60">{label}</span>
    <input type={type} value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={testId} className="mt-1 block w-full border-2 border-[#0A0A0A] py-2 px-3 bg-white text-sm" />
  </label>
);
